import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import "@/i18n";
import { SUPPORTED_LANGS, detectDefaultLang } from "@/i18n";
import { useLenis } from "@/lib/useLenis";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FloatingCTA } from "@/components/layout/FloatingCTA";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Analytics } from "@/components/Analytics";

import Home from "@/pages/Home";
import Features from "@/pages/Features";
import UseCaseDetail from "@/pages/UseCaseDetail";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import Pro from "@/pages/Pro";
import Tutorials from "@/pages/Tutorials";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Faq from "@/pages/Faq";
import Press from "@/pages/Press";
import Testimonials from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";

const RootRedirect = () => {
  const lang = detectDefaultLang();
  return <Navigate to={`/${lang}`} replace />;
};

const LangLayout = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const valid = SUPPORTED_LANGS.includes(lang);
    const target = valid ? lang : "fr";
    i18n.changeLanguage(target);
    localStorage.setItem("fastority_lang", target);
    document.documentElement.lang = target;
  }, [lang, i18n]);

  if (!SUPPORTED_LANGS.includes(lang)) {
    const rest = window.location.pathname.split("/").slice(2).join("/");
    return <Navigate to={`/fr${rest ? `/${rest}` : ""}`} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-[76px]">
        <Outlet />
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

function App() {
  useLenis();

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Analytics />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/:lang" element={<LangLayout />}>
            <Route index element={<Home />} />
            <Route path="features" element={<Features />} />
            <Route path="use-cases/:slug" element={<UseCaseDetail />} />
            <Route path="about" element={<About />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="pro" element={<Pro />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogArticle />} />
            <Route path="faq" element={<Faq />} />
            <Route path="press" element={<Press />} />
            <Route path="testimonials" element={<Testimonials />} />
            <Route path="contact" element={<Contact />} />
            <Route path="legal/:page" element={<Legal />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
